import React, { useEffect, useRef, useState } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  LightBulbIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';

type FeedbackType = 'success' | 'error' | 'warning' | 'info' | 'default';
const feedbackMap: { [key: string]: FeedbackConfig } = {
  error: {
    bg: 'bg-error-base/5 dark:bg-red-950/20',
    border: 'border-error-base dark:border-red-500',
    text: '',
    headingText: 'text-error-base dark:text-red-400 font-semibold',
    icon: <XCircleIcon className="text-error-base dark:text-red-400 h-6 w-6" />,
  },
  info: {
    bg: 'bg-blue-700/5 dark:bg-blue-950/20',
    border: 'border-blue-700 dark:border-blue-500',
    text: '',
    headingText: 'text-blue-700 dark:text-blue-400 font-semibold',
    icon: <InformationCircleIcon className="h-6 w-6 text-blue-700 dark:text-blue-400" />,
  },
  warning: {
    bg: 'bg-yellow-50 dark:bg-yellow-950/20',
    border: 'border-yellow-500 dark:border-yellow-600',
    text: '',
    headingText: 'text-yellow-600 dark:text-yellow-400 font-semibold',
    icon: <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500 dark:text-yellow-400" />,
  },
  success: {
    bg: 'bg-positive-base/5 dark:bg-green-950/20',
    border: 'border-positive-base dark:border-green-500',
    text: '',
    headingText: 'text-positive-base dark:text-green-400 font-semibold',
    icon: <CheckCircleIcon className="text-positive-base dark:text-green-400 h-6 w-6" />,
  },
  default: {
    bg: 'bg-gray-50 dark:bg-gray-800/5',
    border: 'border-gray-300 dark:border-gray-600',
    text: 'text-gray-500 dark:text-gray-400',
    headingText: 'text-gray-500 dark:text-gray-400 font-semibold',
    icon: <LightBulbIcon className="h-6 w-6 text-gray-500 dark:text-gray-400" />,
  },
} as const;

export type FeedbackConfig = {
  bg: string;
  border: string;
  text: string;
  headingText: string;
  icon: React.ReactNode;
};

export type FeedbackProps = {
  /**
   * Use `dismissible` to allow feedback component to managed closing state internally.
   */
  dismissible?: boolean;
  focusOnMount?: boolean;
  children?: React.ReactNode;
  variant?: FeedbackType;
  title?: React.ReactNode;
  description?: React.ReactNode;
  hideIcon?: boolean;
  feedbackConfig?: FeedbackConfig;
  wrapperClassName?: string;
  /**
   * Use `onDismiss` to handle closing state outside of feedback component.
   */
  onDismiss?: () => void;
};

export function Feedback({
  dismissible = false,
  focusOnMount = false,
  variant = 'default',
  feedbackConfig,
  hideIcon = false,
  title,
  description,
  wrapperClassName = '',
  children,
  onDismiss,
}: FeedbackProps) {
  const [visible, setVisible] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  const config =
    feedbackConfig || feedbackMap[variant] || feedbackMap['default'];

  useEffect(() => {
    if (focusOnMount && ref.current) {
      ref.current.scrollIntoView();
    }
  }, [focusOnMount]);

  return visible ? (
    <div
      ref={ref}
      className={`rounded-card flex w-full scroll-m-11 gap-4 border p-4 ${config.border} ${config.bg} ${wrapperClassName} border-container-border`}
    >
      {!hideIcon && <div>{config.icon}</div>}
      <div className="flex flex-grow flex-col gap-2">
        {title && <div className={`${config.headingText || ''}`}>{title}</div>}
        {(description || children) && (
          <div className={`${config.text || 'text-gray-600 dark:text-gray-400'} `}>
            {description}
            {children}
          </div>
        )}
      </div>
      {(dismissible || onDismiss) && (
        <div className="ml-4">
          <button
            onClick={() => {
              onDismiss ? onDismiss() : setVisible(false);
            }}
          >
            <XMarkIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300" />
          </button>
        </div>
      )}
    </div>
  ) : (
    <></>
  );
}
